import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import type { GoodsReceiptFilters } from '@/features/purchasing-receipts/api'
import { ReceiptList } from '@/features/purchasing-receipts/receipt-list'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the goods receipt list. `receipts.$receiptId.index.tsx` is
 * a CHILD of this route in flat file routing — without the Outlet below its
 * URL renders this list instead of itself (apps/company/AGENTS.md).
 */
export const Route = createFileRoute('/_authenticated/purchasing/receipts')({
  validateSearch: (search: Record<string, unknown>): GoodsReceiptFilters => ({
    supplier: typeof search.supplier === 'string' && search.supplier ? search.supplier : undefined,
    warehouse:
      typeof search.warehouse === 'string' && search.warehouse ? search.warehouse : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'goods_receipt.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: PurchasingReceiptsRoute,
})

function PurchasingReceiptsRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const params = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <ReceiptList
      params={params}
      onParamsChange={(next) => void navigate({ to: '/purchasing/receipts', search: next })}
    />
  )
}
